/**
 * Twee gedeelde bewegingspatronen voor het hele thema.
 *
 * In de Next-site deed framer-motion dit met een eigen useScroll-hook per
 * component: 23 van de 33 componenten hadden er een. Dat zijn evenveel
 * scroll-listeners. Hier is het een IntersectionObserver voor het invaren en
 * een enkele rAF-lus voor alle parallax-elementen samen.
 */

/* Invaren: [data-reveal] wordt zichtbaar zodra het element in beeld komt.
   Met data-reveal-delay in seconden voor een trapsgewijze volgorde. */
const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.dataset.revealed = "true";
      // Eenmalig, net als viewport={{ once: true }} in het origineel.
      revealObserver.unobserve(entry.target);
    }
  },
  // -80px komt overeen met de margin die de Next-componenten gebruikten.
  { rootMargin: "-80px 0px" },
);

function observeReveals(root = document) {
  root.querySelectorAll("[data-reveal]:not([data-revealed])").forEach((el) => {
    revealObserver.observe(el);
  });
}

/* Parallax: [data-parallax] verschuift verticaal met de scrollpositie.
   data-parallax is de uitslag in procent van de elementhoogte; de waarde
   loopt van +uitslag als het element onderin beeld komt naar -uitslag als
   het er bovenuit gaat. */
let parallaxItems = [];
let fillItems = [];
let ticking = false;

function collectParallax(root = document) {
  parallaxItems = Array.from(document.querySelectorAll("[data-parallax]"));
  fillItems = Array.from(document.querySelectorAll("[data-fill]"));
  if (parallaxItems.length) updateParallax();
  if (fillItems.length) updateFill();
}

/* Vullende kop: [data-fill] loopt van omtrek naar vlak terwijl je scrollt.
   Het bereik komt uit FillOnScroll.tsx: begint als de kop op 90% van het
   scherm staat, is vol zodra hij op 35% staat. */
function updateFill() {
  const viewport = window.innerHeight;

  for (const el of fillItems) {
    const top = el.getBoundingClientRect().top;
    const progress = (0.9 * viewport - top) / (0.55 * viewport);
    const clamped = Math.min(Math.max(progress, 0), 1);
    el.style.setProperty("--fill", `${(1 - clamped) * 100}%`);
  }
}

function updateParallax() {
  const viewport = window.innerHeight;

  for (const el of parallaxItems) {
    const rect = el.getBoundingClientRect();
    // 0 als het element net onderin binnenkomt, 1 als het er bovenuit gaat.
    const progress = (viewport - rect.top) / (viewport + rect.height);
    const range = Number(el.dataset.parallax) || 0;
    const shift = (0.5 - Math.min(Math.max(progress, 0), 1)) * 2 * range;
    el.style.setProperty("--parallax-y", `${shift}%`);
  }
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    updateParallax();
    updateFill();
    ticking = false;
  });
}

const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
const animationsOff = () =>
  document.documentElement.dataset.animations === "off" || reduced.matches;

function start() {
  observeReveals();
  if (!animationsOff()) {
    collectParallax();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
  }
}

start();

// De theme editor rendert secties opnieuw zonder de pagina te herladen.
document.addEventListener("shopify:section:load", (event) => {
  observeReveals(event.target);
  if (!animationsOff()) collectParallax();
});
